import { useMemo, useState } from 'react';

import {
  type CyclePreviewResponse,
  type SnapshotDetailResponse,
  type SnapshotRowResponse,
} from '../../api';
import {
  type Column,
  DataTable,
  ErrorState,
  LoadingState,
  Pagination,
  usePagination,
} from '../../components';
import { formatCpf, formatInteger } from '../../format';
import { useSnapshot } from '../queries';
import { MOVEMENT_LABELS, PREVIEW_PAGE_SIZE, REVIEW_COPY } from './reviewQueue';

type View = 'differences' | 'file';
type DifferenceKind = 'all' | 'added' | 'removed' | 'changed';

/**
 * The preview read two ways: what moved, and the file itself.
 *
 * **Diferenças** is the comparison the preview response already carries, so it
 * works whatever else fails. **Arquivo** is the snapshot as the insurer would
 * receive it, read on demand from `GET /snapshots/{id}` -- only when somebody
 * opens it, because it is the whole population in the clear and the
 * comparison is usually all an analyst needs.
 *
 * Each table is paged in the browser over rows already loaded. The paged table
 * is keyed by the view, the kind filter and the snapshot's digest, so switching
 * any of them -- a rebuild included, whether the analyst asked for it or a
 * decision did -- starts again on page 1 with the new rows rather than on page
 * 4 of a list that may now have one.
 */
export function CyclePreviewViews({
  built,
  companyId,
}: {
  built: CyclePreviewResponse;
  companyId: string;
}) {
  const [view, setView] = useState<View>('differences');
  const [kind, setKind] = useState<DifferenceKind>('all');
  const digest = built.snapshot.file_sha256;

  const differences = useMemo(() => comparisonRows(built), [built]);
  const shown = useMemo(
    () => (kind === 'all' ? differences : differences.filter((row) => row.kind === kind)),
    [differences, kind],
  );

  return (
    <div className="preview-views">
      <Segmented
        label={REVIEW_COPY.previewViewsLabel}
        value={view}
        onChange={setView}
        options={[
          { value: 'differences', label: REVIEW_COPY.differencesView },
          { value: 'file', label: REVIEW_COPY.fileView },
        ]}
      />

      {view === 'differences' ? (
        <>
          <Segmented
            label={REVIEW_COPY.differenceKindLabel}
            value={kind}
            onChange={setKind}
            options={[
              { value: 'all', label: REVIEW_COPY.allDifferences },
              { value: 'added', label: REVIEW_COPY.addedDifferences },
              { value: 'removed', label: REVIEW_COPY.removedDifferences },
              { value: 'changed', label: REVIEW_COPY.changedDifferences },
            ]}
          />
          <PagedTable
            key={`differences:${kind}:${digest}`}
            caption={REVIEW_COPY.differencesCaption}
            columns={DIFFERENCE_COLUMNS}
            rows={shown}
            emptyTitle={
              kind === 'all' ? REVIEW_COPY.noDifferences : REVIEW_COPY.noDifferencesOfKind
            }
          />
        </>
      ) : (
        <FileView key={`file:${digest}`} built={built} companyId={companyId} />
      )}
    </div>
  );
}

/** The file the cutoff would produce, in the columns of the contract's own layout. */
function FileView({ built, companyId }: { built: CyclePreviewResponse; companyId: string }) {
  const snapshot = useSnapshot({
    companyId,
    snapshotId: built.snapshot.id,
    sha256: built.snapshot.file_sha256,
  });
  const data = snapshot.data;

  const rows = useMemo(
    () => (data === undefined ? [] : [...data.rows].sort((a, b) => a.row_number - b.row_number)),
    [data],
  );
  const columns = useMemo(() => (data === undefined ? [] : fileColumns(data)), [data]);

  if (snapshot.isPending) {
    return <LoadingState label={REVIEW_COPY.fileLoading} />;
  }
  if (data === undefined) {
    return (
      <ErrorState
        error={snapshot.error}
        title={REVIEW_COPY.fileError}
        onRetry={() => void snapshot.refetch()}
      />
    );
  }

  return (
    <>
      <PagedTable
        caption={REVIEW_COPY.fileCaption}
        columns={columns}
        rows={rows}
        emptyTitle={REVIEW_COPY.fileEmpty}
      />
      {data.exceptions.length > 0 && <HeldBack snapshot={data} />}
    </>
  );
}

/** The people the layout would not write: who, in which column and why. */
function HeldBack({ snapshot }: { snapshot: SnapshotDetailResponse }) {
  // The field is a column key; the analyst knows the column by its header.
  const headers = new Map(snapshot.columns.map((column) => [column.key, column.header]));
  const exceptions = [...snapshot.exceptions].sort((a, b) => a.sequence - b.sequence);
  return (
    <section aria-label={REVIEW_COPY.exceptionsTitle}>
      <h3 className="panel__subheading">{REVIEW_COPY.exceptionsTitle}</h3>
      <DataTable
        id="cycle-exceptions"
        caption={REVIEW_COPY.exceptionsCaption}
        columns={[
          { key: 'cpf', header: 'CPF', render: (row) => formatCpf(row.cpf) },
          { key: 'name', header: 'Nome', render: (row) => row.full_name ?? '—' },
          { key: 'field', header: 'Campo', render: (row) => headers.get(row.field) ?? row.field },
          { key: 'reason', header: 'Motivo', render: (row) => row.reason },
        ]}
        rows={exceptions}
        rowKey={(row) => String(row.sequence)}
      />
    </section>
  );
}

/**
 * The layout's columns, in the order and with the headers the API gives.
 *
 * Never a table kept here: every insurer's layout has its own keys, and the
 * response is the only thing that knows which. The movement leads because it is
 * what an analyst scans for; its key is prefixed so a layout with a column
 * keyed `movement` cannot collide with it.
 */
function fileColumns(snapshot: SnapshotDetailResponse): Column<SnapshotRowResponse>[] {
  return [
    {
      key: '__movement',
      header: REVIEW_COPY.movementHeader,
      render: (row) => (row.movement === null ? '—' : (MOVEMENT_LABELS[row.movement] ?? '—')),
    },
    ...snapshot.columns.map((column) => ({
      key: `column:${column.key}`,
      header: column.header,
      render: (row: SnapshotRowResponse) => row.values[column.key] ?? '',
    })),
  ];
}

/**
 * One page of a list, the shared pager under it, and where the page sits in the list.
 *
 * No "Colunas" control: the file and difference columns come from the insurer's
 * layout and differ per insurer, so there is no stable set to choose from.
 */
function PagedTable<TRow>({
  caption,
  columns,
  rows,
  emptyTitle,
}: {
  caption: string;
  columns: readonly Column<TRow>[];
  rows: readonly TRow[];
  emptyTitle: string;
}) {
  const pagination = usePagination(rows, PREVIEW_PAGE_SIZE);
  return (
    <>
      <DataTable
        caption={caption}
        columnVisibility={false}
        columns={columns}
        rows={pagination.items}
        rowKey={(_row, index) => String((pagination.page - 1) * pagination.pageSize + index)}
        emptyTitle={emptyTitle}
      />
      <Pagination
        page={pagination.page}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onPageChange={pagination.setPage}
      />
      {pagination.total > 0 && (
        <p className="panel__footnote">
          {rangeFootnote(pagination.page, pagination.pageSize, pagination.total)}
        </p>
      )}
    </>
  );
}

/** "1–50 de 312 linha(s)." */
function rangeFootnote(page: number, pageSize: number, total: number): string {
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return `${formatInteger(first)}–${formatInteger(last)} de ${formatInteger(total)} linha(s).`;
}

/**
 * A choice between a few things, as a row of pressed and unpressed buttons.
 *
 * Buttons with `aria-pressed` rather than tabs: what changes underneath is the
 * content of one region, not a set of panels, and a toggle button is what a
 * screen reader announces for that.
 */
function Segmented<TValue extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: TValue;
  options: readonly { value: TValue; label: string }[];
  onChange: (value: TValue) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="segmented__option"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface DifferenceRow {
  readonly kind: Exclude<DifferenceKind, 'all'>;
  readonly summary: string;
  readonly detail: string;
}

const KIND_LABELS: Record<DifferenceRow['kind'], string> = {
  added: 'Incluída',
  removed: 'Removida',
  changed: 'Alterada',
};

const DIFFERENCE_COLUMNS: readonly Column<DifferenceRow>[] = [
  { key: 'kind', header: 'Diferença', render: (row) => KIND_LABELS[row.kind] },
  { key: 'values', header: 'Linha', render: (row) => row.summary },
  { key: 'detail', header: 'O que mudou', render: (row) => row.detail },
];

/**
 * The comparison flattened into rows a table can show.
 *
 * The rendered cells are joined rather than given a column each, because the
 * columns belong to the insurer's layout and differ per insurer -- a table with
 * a column per key would be a table that breaks the first time a second layout
 * is configured. The Arquivo view has the columns, read from the API.
 */
function comparisonRows(built: CyclePreviewResponse): DifferenceRow[] {
  const summarise = (values: Record<string, string>) => Object.values(values).join(' · ');
  return [
    ...built.comparison.added.map((person) => ({
      kind: 'added' as const,
      summary: summarise(person.values),
      detail: '—',
    })),
    ...built.comparison.removed.map((person) => ({
      kind: 'removed' as const,
      summary: summarise(person.values),
      detail: '—',
    })),
    ...built.comparison.changed.map((person) => ({
      kind: 'changed' as const,
      summary: summarise(person.values),
      // `changes` is empty on everybody but a changed person, and the generated
      // type makes it optional because the field carries a default. Both read as
      // "nothing moved", which is what the dash says.
      detail:
        (person.changes ?? [])
          .map((change) => `${change.field}: ${change.before} → ${change.after}`)
          .join('; ') || '—',
    })),
  ];
}
